import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tag } from "antd";
import Header from "../../../components/common/Header";
import useDataTable from "../../../components/common/DataTable";
import {
  fetchCustomFields,
  deleteCustomField,
} from "../../../api/redux/action/CustomFieldActions";
import { exportRequest } from "../../../api/network/axios/exportRequest";
import CustomFieldActionView from "../../../components/common/ActionMenu";
import useNotification from "../../../components/common/ResultNotifier";
import { hasPermission } from "../../layout/Constants";
import { yesNoColours } from "../../layout/Constants";
import { useHistory } from "react-router-dom";
function getColor(value) {
  value ? (value = "yes") : (value = "no");
  return yesNoColours[value].color;
}

function getDisplayName(value) {
  value ? (value = "yes") : (value = "no");
  return yesNoColours[value].displayName;
}

function CustomFields() {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState();
  const [exportAllLoading, setExportAllLoading] = useState(false);
  const [flattenedAllData, setFlattenedAllData] = useState([]);
  const [dataTable, setDataTable] = useState();
  const [sortedInfo, setSortedInfo] = useState({
    columnKey: null,
    order: null,
  });

  let history = useHistory();

  const {
    customFields,
    allCustomFields,
    error,
    requestStatus,
    loading,
    user,
    language,
    englishLang,
    selectedTenant,
  } = useSelector((store) => ({
    customFields: store.customFieldReducer.customFields,
    allCustomFields: store.customFieldReducer.allCustomFields,
    error: store.customFieldReducer.error,
    requestStatus: store.customFieldReducer.requestStatus,
    loading: store.customFieldReducer.loading,
    user: store.authReducer.user,
    language: store.langReducer.language,
    englishLang: store.langReducer.englishLang,
    selectedTenant: store.authReducer.selectedTenant,
  }));

  const columns = [
    {
      title: language?.NAME?.value ?? englishLang?.NAME?.value,
      dataIndex: "name",
      sorter: true,
      sortField: "name",
      sortOrder: sortedInfo.columnKey === "name" && sortedInfo.order,
      render: (name) => (
        <a
          onClick={() => {
            const viewOption = "disabled";

            history.push({
              pathname: "/update-customField",
              state: { selectedRow, viewOption },
            });
          }}
        >
          {name}
        </a>
      ),
    },
    {
      title: language?.FIELD_TYPE?.value ?? englishLang?.FIELD_TYPE?.value,
      dataIndex: "type",
      sorter: true,
      sortField: "type",
      sortOrder: sortedInfo.columnKey === "type" && sortedInfo.order,
    },
    {
      title: language?.REQUIRED?.value ?? englishLang?.REQUIRED?.value,
      dataIndex: "required",
      render: (required) => (
        <Tag color={getColor(required)} key={required}>
          {getDisplayName(required)}
        </Tag>
      ),
      sorter: true,
      sortField: "required",
      sortOrder: sortedInfo.columnKey === "required" && sortedInfo.order,
    },
    {
      title: language?.TYPE?.value ?? englishLang?.TYPE?.value,
      dataIndex: "host",
      sorter: true,
      sortField: "host",
      sortOrder: sortedInfo.columnKey === "host" && sortedInfo.order,
    },
  ];

  const [allowPermission, setAllowPermission] = useState({
    edit: hasPermission(selectedTenant, "UPDATE_CUSTOM_FIELDS"),
    delete: hasPermission(selectedTenant, "DELETE_CUSTOM_FIELDS"),
  });

  const [ResultNotifier, isLoading] = useNotification(error, requestStatus);

  const handleSingleDelete = (selected) => {
    dispatch(deleteCustomField(selected.customFieldId));
  };

  const {
    DataTable,
    hasSelected,
    currentPage,
    pageSize,
    selectedRow,
    searchFormHelperHandler,
  } = useDataTable({
    columns,
    source: "customField",
    dataSource: customFields,
    updateEntityPath: "update-customField",
    customActionView:
      allowPermission.edit || allowPermission.delete
        ? CustomFieldActionView
        : "",
    searchText,
    loading: loading,
    handleSingleDelete,
    allowPermission,
    setSortedInfo,
    title: language?.CUSTOM_FILEDS?.value ?? englishLang?.CUSTOM_FILEDS?.value,
  });

  useEffect(() => {
    dispatch(
      fetchCustomFields({
        page: searchFormHelperHandler(),
        size: pageSize,
        searchText: searchText,
        sort: sortedInfo.columnKey
          ? sortedInfo.columnKey +
            "," +
            (sortedInfo.order == "ascend" ? "ASC" : "DESC")
          : null,
      })
    );
  }, [currentPage, isLoading, searchText, sortedInfo]);

  useEffect(() => {
    setDataTable(DataTable);
  }, [loading, selectedRow, language]);

  const handleExportAllButtonClick = () => {
    setExportAllLoading(true);
    const exportName = "export-custom-fields";
    let options = {
      url: "/api/web/custom-fields/export",
      method: "GET",
      responseType: "blob", // important\
      withCredentials: true,
      headers: {
        Accept: "application/vnd.handson.rfid.v5+json",
      },
    };
    exportRequest(options, setExportAllLoading, exportName);
  };

  let flattenedData = [];

  if (Object.keys(customFields).length !== 0) {
    flattenedData = Object.values(customFields.content);
    flattenedData.forEach((data) => {
      data.requiredValue = data.required ? "YES" : "NO";
    });
  }

  useEffect(() => {
    if (allCustomFields && Object.keys(allCustomFields).length !== 0) {
      const flatData = Object.values(allCustomFields.content);

      flatData.forEach((data) => {
        data.requiredValue = data.required ? "YES" : "NO";
      });

      setFlattenedAllData(Object.values(flatData));
    }
  }, [allCustomFields]);

  const headers = [
    { label: language?.NAME?.value ?? englishLang?.NAME?.value, key: "name" },
    { label: language?.TYPE?.value ?? englishLang?.TYPE?.value, key: "type" },
    {
      label: language?.REQUIRED?.value ?? englishLang?.REQUIRED?.value,
      key: "requiredValue",
    },
  ];

  const addNewPath = hasPermission(selectedTenant, "CREATE_CUSTOM_FIELDS")
    ? "add-customField"
    : "";

  return (
    <>
      <div style={{ padding: 24, background: "#fff", minHeight: 20 }}>
        <Header
          addNewPath={addNewPath}
          hasSelected={hasSelected}
          exportTableTitle={
            language?.CUSTOMFILEDS?.value ?? englishLang?.CUSTOMFILEDS?.value
          }
          addNewText={
            language?.ADD_CUSTOM_FILED?.value ??
            englishLang?.ADD_CUSTOM_FILED?.value
          }
          title={
            language?.CUSTOM_FILEDS?.value ?? englishLang?.CUSTOM_FILEDS?.value
          }
          csvData={flattenedData}
          csvAllData={flattenedAllData}
          csvHeaders={headers}
          exportAllButtonClick={handleExportAllButtonClick}
          exportAllLoading={exportAllLoading}
          loading={loading}
          exportButton={true}
          exportAllButton={true}
          handleSearch={setSearchText}
        />
        {dataTable}
        <ResultNotifier />
      </div>
    </>
  );
}

export default CustomFields;
